import { useEffect } from "react";
import { useGLTF } from "@react-three/drei";

import {
  Animation3DInteractionType,
  BaseAnimationWithAssetProps,
} from "../../types";
import VideoAnimation from "./VideoAnimation";
import {
  useIsAnimationPlaying,
  useIsAutoplayAndVisible,
} from "../../../../hooks";
import { useAssignCustomShaderMaterial } from "../../../../hooks/useAssignCustomShaderMaterial";
import { useIsPinchtoZoom } from "../../../../hooks/useIsPinchtoZoom";
import { useCameraTransform } from "../../../../hooks/useCameraTransform";
import { useCameraControlsRef } from "../../../../hooks/useCameraControlsRef";

export type Props = BaseAnimationWithAssetProps & {
  videoAsset: {
    url: string;
  };
} & Animation3DInteractionType;

const VideoAnimationWithAsset: React.FC<Props> = ({
  interactionType,
  asset,
  videoAsset,
  transform3D,
  customMaterials,
}) => {
  // This is a custom hook that returns true if the authorable product is set to autoplay and is visible
  // Will be removed in the future when we implement a better controls system and is used only for pairity with OF1 at the moment
  const isAutoPlayingAndVisible = useIsAutoplayAndVisible();
  const { setIsAnimationPlaying, isAnimationPlaying } = useIsAnimationPlaying();
  const { isPinchZoom } = useIsPinchtoZoom();
  const { cameraPos, cameraRot } = useCameraTransform();
  const { scene } = useGLTF(asset.url);
  const cameraControlsRef = useCameraControlsRef();

  const customMaterialClone = useAssignCustomShaderMaterial(
    scene,
    customMaterials,
  );

  // Small effect that ensures on mobile when modal is unexapnded (i.e when isAutoPlayingAndVisible is enabled)
  // We play the video animation by default once (without user interaction) and then transition to the 3D model
  useEffect(() => {
    if (isAutoPlayingAndVisible) {
      setIsAnimationPlaying!(true);
    }
  }, [isAutoPlayingAndVisible]);

  const props = {
    [interactionType]: () => {
      if (isPinchZoom) {
        if (cameraPos && cameraRot && cameraControlsRef?.current) {
          const position = cameraPos as [number, number, number];
          const rotation = cameraRot as [number, number, number];
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          cameraControlsRef.current
            .setLookAt(...position, ...rotation, true)
            .then(() => {
              setIsAnimationPlaying!(true);
            });
        }
      } else {
        setIsAnimationPlaying!(true);
      }
    },
  };

  return (
    <>
      <VideoAnimation
        videoSrc={videoAsset.url}
        visible={isAnimationPlaying}
        onAnimationComplete={() => {
          setIsAnimationPlaying!(false);
        }}
      />
      {/* We wrap the primitive in a group so we can apply the event handlers without facing this bug NVS-3626 */}
      <group>
        <primitive
          {...props}
          object={customMaterialClone}
          position={transform3D?.position}
          rotation={transform3D?.rotation}
          scale={transform3D?.scale}
        />
      </group>
    </>
  );
};

export default VideoAnimationWithAsset;
